import api from "./api";

export async function createDebate(goal, opts = {}) {
  const body = { goal };
  if (opts.enabled_agent_ids) body.enabled_agent_ids = opts.enabled_agent_ids;
  if (opts.mode) body.mode = opts.mode;
  const { data } = await api.post("/api/debates", body);
  return data;
}

export async function listDebates(limit = 20) {
  const { data } = await api.get("/api/debates", { params: { limit } });
  return data;
}

export async function getDebate(sessionId) {
  const { data } = await api.get(`/api/debates/${sessionId}`);
  return data;
}

export async function cancelDebate(sessionId) {
  const { data } = await api.post(`/api/debates/${sessionId}/cancel`);
  return data;
}

export async function deleteDebate(sessionId) {
  const { data } = await api.delete(`/api/debates/${sessionId}`);
  return data;
}

/**
 * Stream a debate's SSE events. Uses fetch + ReadableStream so cookies are
 * sent reliably (native EventSource has spotty `withCredentials` behaviour
 * and can't pass our SameSite=None cross-site cookie everywhere).
 *
 * Returns { close } — call close() to abort.
 */
export function streamDebate(sessionId, handlers = {}) {
  const url = `${process.env.REACT_APP_BACKEND_URL}/api/debates/${sessionId}/stream`;
  const ac = new AbortController();

  const safeCall = (name, payload) => {
    try {
      handlers[name]?.(payload);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("[streamDebate handler error]", name, e);
    }
  };

  (async () => {
    let buf = "";
    try {
      const resp = await fetch(url, {
        method: "GET",
        credentials: "include",
        headers: { Accept: "text/event-stream" },
        signal: ac.signal,
      });
      if (!resp.ok || !resp.body) {
        safeCall("onError", { detail: `HTTP ${resp.status}` });
        safeCall("onClose");
        return;
      }
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        // SSE messages are separated by \n\n
        let idx;
        while ((idx = buf.indexOf("\n\n")) !== -1) {
          const raw = buf.slice(0, idx);
          buf = buf.slice(idx + 2);
          const evt = parseSSE(raw);
          if (evt) dispatch(evt, safeCall);
        }
      }
      // Flush any trailing message without final blank line
      if (buf.trim()) {
        const evt = parseSSE(buf);
        if (evt) dispatch(evt, safeCall);
      }
    } catch (e) {
      if (e.name !== "AbortError") {
        safeCall("onError", { detail: e.message || String(e) });
      }
    } finally {
      safeCall("onClose");
    }
  })();

  return {
    close: () => ac.abort(),
  };
}

function parseSSE(raw) {
  let event = "message";
  let data = null;
  for (const line of raw.split("\n")) {
    if (line.startsWith("event:")) event = line.slice(6).trim();
    else if (line.startsWith("data:")) {
      const d = line.slice(5).trim();
      try {
        data = JSON.parse(d);
      } catch {
        data = d;
      }
    }
  }
  if (data === null) return null;
  return { event, data };
}

function dispatch({ event, data }, safeCall) {
  switch (event) {
    case "session_start":
      safeCall("onSessionStart", data);
      break;
    case "agent_start":
      safeCall("onAgentStart", data);
      break;
    case "agent_token":
      safeCall("onAgentToken", data);
      break;
    case "agent_end":
      safeCall("onAgentEnd", data);
      break;
    case "decision_start":
      safeCall("onDecisionStart", data);
      break;
    case "decision_token":
      safeCall("onDecisionToken", data);
      break;
    case "decision_end":
      safeCall("onDecisionEnd", data);
      break;
    case "tasks_generated":
      safeCall("onTasksGenerated", data);
      break;
    case "complete":
      safeCall("onComplete", data);
      break;
    case "cancelled":
      safeCall("onCancelled", data);
      break;
    case "error":
      safeCall("onError", data);
      break;
    default:
      safeCall("onUnknown", { event, data });
  }
}
